import { useEffect, useState, MouseEvent } from 'react'

import { faFilter as faFilterRegular } from '@fortawesome/pro-regular-svg-icons'
import { faFilter as faFilterSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  ListSubheader,
  Popover,
  Typography,
} from '@material-ui/core'

import { useCurrentSeniorYear } from 'store/current-senior-year/hooks'

import {
  HISTORIC_FOLLOWER_YEARS,
  HistoricFollowerYear,
  getHistoricFollowerGradYear,
  getHistoricFollowerLabel,
} from './gradYears'

interface HistoricFollowersColumnHeaderProps {
  title: string
  selectedYears: HistoricFollowerYear[]
  onChange: (next: HistoricFollowerYear[]) => void
}

const HistoricFollowersColumnHeader = ({ title, selectedYears, onChange }: HistoricFollowersColumnHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [pending, setPending] = useState<HistoricFollowerYear[]>(selectedYears)
  const isFiltered = selectedYears.length !== HISTORIC_FOLLOWER_YEARS.length
  const currentSeniorYear = useCurrentSeniorYear()

  useEffect(() => {
    if (anchorEl) return
    setPending(selectedYears)
  }, [anchorEl, selectedYears])

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setPending(selectedYears)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const togglePending = (year: HistoricFollowerYear) => {
    const isSelected = pending.includes(year)
    const next = isSelected ? pending.filter((y) => y !== year) : [...pending, year]
    setPending(HISTORIC_FOLLOWER_YEARS.filter((y) => next.includes(y)))
  }

  const handleApply = () => {
    onChange(pending)
    setAnchorEl(null)
  }

  const caption = selectedYears.length
    ? selectedYears.map((y) => getHistoricFollowerGradYear(y, currentSeniorYear)).join(' • ')
    : 'None'

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <IconButton
        size="small"
        onClick={handleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={`${title} class year selection`}
        color={isFiltered ? 'primary' : 'default'}
        style={{ padding: 2 }}
      >
        <FontAwesomeIcon
          icon={isFiltered ? faFilterSolid : faFilterRegular}
          size="xs"
        />
      </IconButton>
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
        <span
          aria-hidden
          style={{ fontSize: 11, fontWeight: 400, visibility: 'hidden', whiteSpace: 'nowrap' }}
        >
          {caption}
        </span>
        <span style={{ fontWeight: 500 }}>{title}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.54)',
            whiteSpace: 'nowrap',
          }}
        >
          {caption}
        </span>
      </span>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box width={260}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pr={2}
          >
            <ListSubheader disableSticky>Classes</ListSubheader>
            <Button
              variant="text"
              size="small"
              color="primary"
              onClick={() => setPending([...HISTORIC_FOLLOWER_YEARS])}
              disabled={pending.length === HISTORIC_FOLLOWER_YEARS.length}
            >
              Select all
            </Button>
          </Box>
          <FormGroup style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
            {HISTORIC_FOLLOWER_YEARS.map((year) => (
              <FormControlLabel
                key={year}
                control={
                  <Checkbox
                    checked={pending.includes(year)}
                    onChange={() => togglePending(year)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 500 }}
                  >
                    {getHistoricFollowerLabel(year, currentSeniorYear)}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
          <Divider />
          <Box
            display="grid"
            gridGap={8}
            p={1.5}
            style={{ gridTemplateColumns: '1fr 1fr' }}
          >
            <Button
              variant="text"
              color="primary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </span>
  )
}

export default HistoricFollowersColumnHeader
